import { useEffect, useRef, useState } from 'react';

const defaultMinWidth = 15;
const defaultWidth = 150;
const defaultMaxWidth = 400;

function initSizes(columns:Array<any>) {
  const result:Array<any> = [];

  columns.forEach(c => {
    const width = !c.width || c.width < 0 ? defaultWidth : c.width;
    
    let minWidth = !c.minWidth || c.minWidth < 0 ? defaultMinWidth : c.minWidth;
    if (minWidth > width) {
      minWidth = width;
    }

    let maxWidth = !c.maxWidth || c.maxWidth < 0 ? defaultMaxWidth : c.maxWidth;
    if (maxWidth < width) {
      maxWidth = width;
    }

    result.push({
      id: c.accessor,
      resizable: c.resizable,
      width,
      minWidth,
      maxWidth,
    });
  });

  return result;
}

let originalX:number|null = null;
let originalWidth:number|null = null;
let resizeIndex:number|null = null;
let originalRowWidth:number|null = null;

function getHeaderRowElement(tableId:string) {
  return document.getElementById(`${tableId}-header-row`);
}

function getHeaderElement(tableId:string, index:number) {
  return document.getElementById(`${tableId}-header-${index}`);
}

function resizeHeaderRow(tableId:string, delta:number) {
  let element = getHeaderRowElement(tableId);

  if (element) {
    element.style.width = (originalRowWidth as number + delta) + 'px';
  }
}

function resizeTableRows(tableId:string, delta:number) {
  let row = 0;
  let element = document.getElementById(`${tableId}-row-${row}`);

  while (element) {
    element.style.width = (originalRowWidth as number + delta) + 'px';
    row = row + 1;
    element = document.getElementById(`${tableId}-row-${row}`);
  }
}

function resizeColumn(tableId:string, columnIndex:number, width:number) {
  const headerElement = getHeaderElement(tableId, columnIndex);
  if (!headerElement) {
    return;
  }
  headerElement.style.width = width + 'px';

  let row = 0;
  let element = document.getElementById(`${tableId}-cell-${row}-${columnIndex}`);

  while (element) {
    element.style.width = width + 'px';
    row = row + 1;
    element = document.getElementById(`${tableId}-cell-${row}-${columnIndex}`);
  }
}

export default function useFixResizer(tableId:string, columns:Array<any>, onColumnsResized:any) {
  const initColSizes = initSizes(columns);
  const [columnSizes, _setColumnSizes] = useState<Array<any>>(initColSizes);

  const cRef = useRef(initColSizes);

  useEffect(() => {
    originalX = null;
    originalWidth = null;
    resizeIndex = null;
    originalRowWidth = null;

    setColumnSizes(initSizes(columns));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  function setColumnSizes(s:any) {
    cRef.current = s;
    _setColumnSizes(s);
  }

  function resize(e:MouseEvent) {
    const delta = e.pageX - (originalX as number);

    const newWidth = (originalWidth as number) + delta;

    if (
      newWidth < cRef.current[resizeIndex as number].minWidth ||
      newWidth > cRef.current[resizeIndex as number].maxWidth 
    ) {
      return;
    }

    resizeHeaderRow(tableId, delta);
    resizeTableRows(tableId, delta);
    resizeColumn(tableId, resizeIndex as number, newWidth);
  }

  function stopResize() {
    window.removeEventListener('mousemove', resize);
    window.removeEventListener('mouseup', stopResize);

    const headerElement = getHeaderElement(tableId, resizeIndex as number);
    if (!headerElement) {
      return;
    }

    const finalWidth = parseFloat(getComputedStyle(headerElement).getPropertyValue('width').replace('px', ''));

    const newColSizes = [...columnSizes];
    newColSizes[resizeIndex as number].width = finalWidth;

    const cbValues = [{
      id: newColSizes[resizeIndex as number].id,
      index: resizeIndex,
      width: finalWidth
    }];

    originalX = null;
    originalWidth = null;
    resizeIndex = null;
    originalRowWidth = null;

    setColumnSizes(newColSizes);
    if (onColumnsResized) {
      onColumnsResized(cbValues);
    }
  }

  function onResizeStart(index:number) {
    if (index < 0 || index >= columnSizes.length) {
      return;
    }

    if (!cRef.current[index].resizable) {
      return;
    }
    
    const headerElement = getHeaderElement(tableId, index);
    const headerRowElement = getHeaderRowElement(tableId);
    if (!headerElement || !headerRowElement) {
      return;
    }
    originalX = headerElement.getBoundingClientRect().right;

    originalWidth = parseFloat(getComputedStyle(headerElement).getPropertyValue('width').replace('px', ''));
    originalRowWidth = parseFloat(getComputedStyle(headerRowElement).getPropertyValue('width').replace('px', ''));

    resizeIndex = index;

    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResize);
  }

  return [columnSizes, onResizeStart];
}